import { FiCheckCircle } from "react-icons/fi";
import SkeletonBase from "newui/components/Skeleton";
import { useMyScores } from "../../../../../hooks/usePanelScoringQueries";
import type { PanelSupplierScores } from "../../../../../types/scoring.type";

interface SubmitConfirmationProps {
	rfxId: number;
	evalId: number;
	submittedAt?: string | null;
}

const formatDate = (dateStr: string | null | undefined): string => {
	if (!dateStr) {
		return "";
	}
	const date = new Date(dateStr);
	return date.toLocaleDateString("en-AU", {
		day: "2-digit",
		month: "short",
		year: "numeric",
		hour: "2-digit",
		minute: "2-digit",
	});
};

const ReadOnlyScoreTable: React.FC<{ suppliers: PanelSupplierScores[] }> = ({ suppliers }) => {
	return (
		<div className="space-y-4">
			{suppliers.map((supplier) => (
				<div
					key={supplier.invite_id}
					className="rounded-lg border border-gray-200 bg-white"
				>
					<div className="border-b border-gray-100 px-4 py-2.5">
						<span className="text-sm font-medium text-gray-900">
							{supplier.supplier_name}
						</span>
					</div>
					<div className="overflow-x-auto">
						<table className="w-full border-collapse text-sm">
							<thead>
								<tr className="text-left text-xs text-gray-400">
									<th className="px-4 py-2 font-medium">Criterion</th>
									<th className="px-4 py-2 font-medium text-right w-20">Weight</th>
									<th className="px-4 py-2 font-medium text-right w-20">Score</th>
									<th className="px-4 py-2 font-medium">Comment</th>
								</tr>
							</thead>
							<tbody>
								{supplier.criteria.map((c) => (
									<tr
										key={c.rfx_criteria_id}
										className="border-t border-gray-50"
									>
										<td className="px-4 py-2 text-gray-700">
											{c.criterion_name}
										</td>
										<td className="px-4 py-2 text-right text-gray-500 tabular-nums">
											{c.criterion_weight}%
										</td>
										<td className="px-4 py-2 text-right font-medium tabular-nums">
											{c.score !== null ? c.score : "—"}
										</td>
										<td className="px-4 py-2 text-gray-500 text-xs max-w-[200px] truncate">
											{c.comment || "—"}
										</td>
									</tr>
								))}
							</tbody>
						</table>
					</div>
				</div>
			))}
		</div>
	);
};

export const SubmitConfirmation: React.FC<SubmitConfirmationProps> = ({
	rfxId,
	evalId,
	submittedAt,
}) => {
	const { data, isLoading } = useMyScores(rfxId, evalId);

	return (
		<div className="space-y-4" data-testid="submit-confirmation">
			<div className="flex items-center gap-3 rounded-lg border border-green-200 bg-green-50 px-4 py-3">
				<FiCheckCircle size={20} className="shrink-0 text-green-600" aria-hidden="true" />
				<div>
					<p className="text-sm font-medium text-green-800">
						Your scores have been submitted
					</p>
					{submittedAt && (
						<p className="text-xs text-green-600">
							Submitted on {formatDate(submittedAt)}
						</p>
					)}
					<p className="mt-1 text-xs text-green-700">
						Your evaluation is complete. The RFx Lead will review all panel
						submissions and proceed to the next step.
					</p>
				</div>
			</div>

			<div>
				<h4 className="mb-3 text-sm font-medium text-gray-700">
					Your submitted scores (read-only)
				</h4>
				{isLoading ? (
					<div className="space-y-2">
						{[1, 2].map((i) => (
							<SkeletonBase key={i} startColor="lightgray" width="100%" height="48px" className="!rounded-lg" />
						))}
					</div>
				) : (
					<ReadOnlyScoreTable suppliers={data?.suppliers ?? []} />
				)}
			</div>
		</div>
	);
};
